import type { CSSProperties } from 'react'
import type { ProfileTableUiVariant } from './types'

interface ProfileTableVariantStyles {
    headerRow: string
    headerCell: string
    headerLabel: string
    kebabButton: string
    sortActive: string
    sortInactive: string
    popover: string
    menuItem: string
    menuSeparator: string
    container: string
    scroll: string
    table: string
    thead: string
    row: string
    expanded: string
    dragGhost: {
        container: string
        header: string
        label: string
        row: string
    }
    headerStyle: (style: CSSProperties, collapsed: boolean) => CSSProperties
}

const legacyMenuItem =
    'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800'
const v2MenuItem = 'flex w-full items-center gap-2 rounded-xl px-3 py-1.5 text-left text-sm text-foreground hover:bg-default'

const defaultStyles: ProfileTableVariantStyles = {
    headerRow: 'bg-slate-50 dark:bg-slate-800',
    headerCell: 'group relative border-b border-r last:border-r-0 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 py-2',
    headerLabel: 'text-[11px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider truncate flex-1 min-w-0',
    kebabButton:
        'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-slate-400 opacity-0 transition-opacity hover:bg-slate-200 hover:text-slate-700 group-hover:opacity-100 focus-visible:opacity-100 dark:text-slate-500 dark:hover:bg-slate-700 dark:hover:text-slate-200',
    sortActive: 'text-blue-600 dark:text-blue-400',
    sortInactive: 'text-slate-300 dark:text-slate-600',
    popover: 'min-w-[200px] rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900',
    menuItem: legacyMenuItem,
    menuSeparator: 'my-1 border-t border-slate-100 dark:border-slate-800',
    container: 'rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden w-full flex flex-col flex-1 min-h-0 bg-white dark:bg-slate-900',
    scroll: 'flex-1 min-h-0 overflow-auto',
    table: 'w-full border-collapse',
    thead: 'sticky top-0 z-10 bg-slate-50 dark:bg-slate-800',
    row: 'group hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer',
    expanded: 'p-0 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800',
    dragGhost: {
        container:
            'overflow-hidden rounded-xl -rotate-[1.8deg] border backdrop-blur-[2px] border-[#b8d1f1] bg-[rgba(248,252,255,0.92)] shadow-[0_18px_44px_rgba(15,23,42,0.34)] dark:border-[rgba(110,139,188,0.45)] dark:bg-[rgba(37,45,56,0.82)]',
        header: 'flex h-[30px] items-center border-b px-2 border-[#d4e4f8] bg-[rgba(235,245,255,0.95)] dark:border-[rgba(110,139,188,0.3)] dark:bg-[rgba(31,37,46,0.9)]',
        label: 'truncate text-[11px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400',
        row: 'h-[44px] rounded-[6px] border border-[rgba(175,201,232,0.52)] bg-[rgba(235,244,255,0.62)] dark:border-[rgba(120,142,172,0.22)] dark:bg-[rgba(55,66,82,0.36)]',
    },
    headerStyle: (style) => style,
}

const equipmentStyles: ProfileTableVariantStyles = {
    ...defaultStyles,
    headerRow: 'border-b border-gray-100 dark:border-[#2D3843] bg-gray-50 dark:bg-[#1A1D1F]',
    headerCell: 'group relative bg-gray-50 dark:bg-[#1A1D1F] py-3',
    headerLabel: 'text-sm font-medium text-gray-500 dark:text-gray-400 truncate flex-1 min-w-0 whitespace-nowrap',
    kebabButton:
        'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-400 opacity-0 transition-opacity hover:bg-gray-100 hover:text-gray-700 group-hover:opacity-100 focus-visible:opacity-100 dark:text-gray-500 dark:hover:bg-[#2D3843] dark:hover:text-gray-200',
    container: 'overflow-hidden w-full flex flex-col flex-1 min-h-0 bg-transparent',
}

const v2Styles: ProfileTableVariantStyles = {
    ...defaultStyles,
    headerRow: 'border-b border-border bg-surface-secondary',
    headerCell: 'group relative border-b border-r last:border-r-0 border-border bg-surface-secondary py-2',
    headerLabel: 'text-[11px] font-medium text-foreground-muted uppercase tracking-wider truncate flex-1 min-w-0',
    kebabButton:
        'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-foreground-muted opacity-0 transition-opacity hover:bg-default hover:text-foreground group-hover:opacity-100 focus-visible:opacity-100',
    sortActive: 'text-accent',
    sortInactive: 'text-foreground-muted',
    popover: 'min-w-[200px] rounded-3xl bg-surface p-1 shadow-overlay',
    menuItem: v2MenuItem,
    menuSeparator: 'my-1 mx-3 border-t border-border',
    container: 'overflow-hidden w-full flex flex-col flex-1 min-h-0 rounded-2xl bg-surface shadow-surface',
    table: 'w-full text-sm border-collapse',
    thead: 'sticky top-0 z-10 bg-surface-secondary',
    row: 'group border-b border-border hover:bg-surface-secondary transition-colors cursor-pointer',
    expanded: 'p-0 border-b border-border bg-surface-secondary',
    dragGhost: {
        container: 'overflow-hidden rounded-xl -rotate-[1.8deg] bg-surface shadow-overlay backdrop-blur-[2px]',
        header: 'flex h-[30px] items-center border-b border-border bg-surface-secondary px-2',
        label: 'truncate text-[11px] font-medium uppercase tracking-wider text-foreground-muted',
        row: 'h-[44px] rounded-[6px] bg-surface-secondary',
    },
}

const dispatchStyles: ProfileTableVariantStyles = {
    ...defaultStyles,
    headerRow: 'h-8 border-b border-[#d6e2ee] bg-[#f8fbff] dark:border-slate-700 dark:bg-slate-900',
    headerCell:
        'group relative h-8 border-b border-r last:border-r-0 border-b-[#d6e2ee] border-r-[#e2eaf3] bg-[#f8fbff] py-0 dark:border-slate-700 dark:bg-slate-900',
    headerLabel: 'text-[10px] font-medium uppercase tracking-[0.06em] text-[#5c7188] dark:text-slate-400 truncate flex-1 min-w-0',
    // Sort arrows track the header palette instead of the legacy blue: the label tone when
    // active, the column-border tone when idle.
    sortActive: 'text-[#5c7188] dark:text-slate-300',
    sortInactive: 'text-[#d6e2ee] dark:text-slate-600',
    container: 'overflow-hidden w-full flex flex-col flex-1 min-h-0 rounded-xl border border-[#d6e2ee] bg-white dark:border-slate-700 dark:bg-slate-900',
    scroll: 'flex-1 min-h-0 overflow-auto dispatch-table-scrollbar',
    table: 'w-full text-sm border-collapse',
    thead: 'sticky top-0 z-10 bg-[#f8fbff] dark:bg-slate-900',
    row: 'group border-b border-[#e2eaf3] hover:bg-[#f8fbff] transition-colors cursor-pointer dark:border-slate-700 dark:hover:bg-slate-800',
    expanded: 'p-0 border-b border-[#e2eaf3] bg-[#f8fbff] dark:border-slate-700 dark:bg-slate-800',
    headerStyle: (style, collapsed) => ({ ...style, padding: collapsed ? '0 4px' : '0 16px' }),
}

export const PROFILE_TABLE_VARIANT_STYLES: Record<ProfileTableUiVariant, ProfileTableVariantStyles> = {
    default: defaultStyles,
    equipment: equipmentStyles,
    v2: v2Styles,
    dispatch: dispatchStyles,
}
